/**
 * TanStack Query hooks backing the admin orders feature.
 *
 * Wraps the typed `api` client where the OpenAPI spec covers an endpoint, and
 * falls back to a direct fetch (with the same Authorization header) for the
 * couple of GETs that exist on the backend but aren't exposed by the
 * generated `paths` map yet:
 *   - `GET /api/v1/admin/orders`         (paginated list with optional `status` filter)
 *   - `GET /api/v1/admin/orders/by-code` (busca do atendimento — RF-COD-06)
 *
 * The fallback path is centralised in `fetchAdminOrdersList` so the hook
 * surface stays clean and the gap is easy to delete once the OpenAPI is
 * regenerated. TODO: remove `fetchAdminOrdersList` after the backend
 * publishes the GET schema for the admin orders list.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '@favelafood/shared/api/client';
import { ApiError } from '@favelafood/shared/api/errors';
import { getAccessToken } from '@favelafood/shared/auth/tokens';
import type { components } from '@favelafood/shared/api/generated/api';

type OrderResponse = components['schemas']['OrderResponse'];
type OrderDetailResponse = components['schemas']['OrderDetailResponse'];
type OrderStatusHistoryResponse = components['schemas']['OrderStatusHistoryResponse'];
type DeliveryResponse = components['schemas']['DeliveryResponse'];
type PageResponseOrder = components['schemas']['PageResponseOrderResponse'];
type OrderStatusEnum = components['schemas']['OrderStatusEnum'];
type AdminCheckoutRequest = components['schemas']['AdminCheckoutRequest'];

export interface OrdersListFilters {
  page: number;
  size: number;
  status?: OrderStatusEnum | undefined;
  /** Free-text search executed client-side over the current page. */
  search?: string | undefined;
}

export const ordersKeys = {
  all: ['orders'] as const,
  lists: () => [...ordersKeys.all, 'list'] as const,
  list: (filters: OrdersListFilters) => [...ordersKeys.lists(), filters] as const,
  detail: (id: string) => [...ordersKeys.all, 'detail', id] as const,
  delivery: (id: string) => [...ordersKeys.all, 'delivery', id] as const,
  history: (id: string) => [...ordersKeys.all, 'history', id] as const,
};

const baseUrl = import.meta.env.VITE_API_BASE ?? '';

/**
 * Direct fetch fallback for `GET /api/v1/admin/orders`.
 *
 * The OpenAPI spec exposes only the POST counterpart, so we hit the endpoint
 * manually and rely on the `OrderResponse` schema for typing the page content.
 */
const fetchAdminOrdersList = async (
  filters: OrdersListFilters,
): Promise<PageResponseOrder> => {
  const params = new URLSearchParams();
  params.set('page', String(filters.page));
  params.set('size', String(filters.size));
  if (filters.status) params.set('status', filters.status);

  const token = getAccessToken();
  const response = await fetch(`${baseUrl}/api/v1/admin/orders?${params.toString()}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (!response.ok) {
    throw new ApiError(response.status, {}, `HTTP ${response.status}`);
  }

  return (await response.json()) as PageResponseOrder;
};

export interface SearchOrderByCodeVars {
  code: string;
  merchantId: string;
}

/**
 * Busca do atendimento por código do pedido (RF-COD-06):
 * `GET /api/v1/admin/orders/by-code`.
 *
 * `merchantId` é obrigatório no backend — o código só é único dentro da loja
 * (reinicia todo dia e embute o prefixo dela), e sem escopo um sequencial de
 * 6 dígitos viraria enumerador de pedido de terceiro. Também não está no
 * OpenAPI gerado, então vai por fetch direto como a lista acima.
 *
 * É mutation e não query porque a busca é um ato do atendente (submit), não
 * um estado da tela: sem submit não há request, e um 404 — que aqui é
 * resultado normal, não falha — não fica pendurado como erro da página.
 */
export const useSearchOrderByCode = (): UseMutationResult<
  OrderDetailResponse,
  Error,
  SearchOrderByCodeVars
> =>
  useMutation({
    mutationFn: async ({ code, merchantId }: SearchOrderByCodeVars) => {
      const params = new URLSearchParams({ code: code.trim(), merchantId });
      const token = getAccessToken();
      const response = await fetch(
        `${baseUrl}/api/v1/admin/orders/by-code?${params.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        },
      );

      if (!response.ok) {
        throw new ApiError(response.status, {}, `HTTP ${response.status}`);
      }

      return (await response.json()) as OrderDetailResponse;
    },
  });

const unwrap = <T,>(data: T | undefined, error: unknown): T => {
  if (error) throw error;
  if (data === undefined) throw new Error('Resposta vazia do servidor');
  return data;
};

export const useOrders = (
  filters: OrdersListFilters,
): UseQueryResult<PageResponseOrder> =>
  useQuery({
    queryKey: ordersKeys.list(filters),
    queryFn: () => fetchAdminOrdersList(filters),
  });

export const useOrder = (orderId: string): UseQueryResult<OrderDetailResponse> =>
  useQuery({
    queryKey: ordersKeys.detail(orderId),
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/orders/{orderId}', {
        params: { path: { orderId } },
      });
      return unwrap(data, error);
    },
    enabled: Boolean(orderId),
  });

export const useOrderDelivery = (
  orderId: string,
): UseQueryResult<DeliveryResponse> =>
  useQuery({
    queryKey: ordersKeys.delivery(orderId),
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/orders/{orderId}/delivery', {
        params: { path: { orderId } },
      });
      return unwrap(data, error);
    },
    enabled: Boolean(orderId),
    // Some orders don't have an associated delivery yet (e.g. CREATED). O backend
    // devolve 422 DELIVERY_NOT_FOUND (BusinessException), não 404 — não é erro de
    // rede/servidor, então não vale a pena tentar de novo.
    retry: false,
    throwOnError: false,
  });

export const useOrderHistory = (
  orderId: string,
): UseQueryResult<OrderStatusHistoryResponse[]> =>
  useQuery({
    queryKey: ordersKeys.history(orderId),
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/orders/{orderId}/history', {
        params: { path: { orderId } },
      });
      return unwrap(data, error);
    },
    enabled: Boolean(orderId),
  });

const invalidateOrder = (queryClient: ReturnType<typeof useQueryClient>, id: string): void => {
  void queryClient.invalidateQueries({ queryKey: ordersKeys.detail(id) });
  void queryClient.invalidateQueries({ queryKey: ordersKeys.delivery(id) });
  void queryClient.invalidateQueries({ queryKey: ordersKeys.history(id) });
  void queryClient.invalidateQueries({ queryKey: ordersKeys.lists() });
};

export interface UpdateStatusVars {
  id: string;
  status: string;
}

export const useUpdateOrderStatus = (): UseMutationResult<
  OrderResponse,
  Error,
  UpdateStatusVars
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: UpdateStatusVars) => {
      const { data, error } = await api.PATCH('/api/v1/admin/orders/{id}/status', {
        params: { path: { id } },
        body: { status },
      });
      return unwrap(data, error);
    },
    onSuccess: (_data, vars) => invalidateOrder(queryClient, vars.id),
  });
};

export interface CancelOrderVars {
  id: string;
  reason?: string;
}

export const useCancelOrder = (): UseMutationResult<OrderResponse, Error, CancelOrderVars> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: CancelOrderVars) => {
      const { data, error } = await api.POST('/api/v1/admin/orders/{id}/cancel', {
        params: { path: { id } },
        body: reason ? { reason } : {},
      });
      return unwrap(data, error);
    },
    onSuccess: (_data, vars) => invalidateOrder(queryClient, vars.id),
  });
};

export interface AssignCourierVars {
  id: string;
  courierProfileId: string;
}

export const useAssignCourier = (): UseMutationResult<
  DeliveryResponse,
  Error,
  AssignCourierVars
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, courierProfileId }: AssignCourierVars) => {
      const { data, error } = await api.POST(
        '/api/v1/admin/orders/{id}/assign-courier',
        {
          params: { path: { id } },
          body: { courierProfileId },
        },
      );
      return unwrap(data, error);
    },
    onSuccess: (_data, vars) => invalidateOrder(queryClient, vars.id),
  });
};

/**
 * Creates an order on behalf of a customer (`POST /api/v1/admin/orders`).
 *
 * Used by the manual order-creation page so an operator can place an order
 * for a phone-in customer when the storefront flow is unreachable.
 *
 * Invalidates the orders list on success so the new order shows up in the
 * `/admin/orders` view; the caller is also expected to navigate to the
 * detail page (`/admin/orders/{id}`) using the response payload.
 */
export const useCreateAdminOrder = (): UseMutationResult<
  OrderDetailResponse,
  Error,
  AdminCheckoutRequest
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: AdminCheckoutRequest) => {
      const { data, error } = await api.POST('/api/v1/admin/orders', { body });
      return unwrap(data, error);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ordersKeys.lists() });
    },
  });
};

export const useConfirmReceipt = (): UseMutationResult<OrderResponse, Error, string> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await api.POST(
        '/api/v1/admin/orders/{id}/confirm-receipt',
        {
          params: { path: { id } },
        },
      );
      return unwrap(data, error);
    },
    onSuccess: (_data, id) => invalidateOrder(queryClient, id),
  });
};
